import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { previewInventoryLabels } from '@/lib/api/print.js';
import { getCurrentStore, updateCurrentStore } from '@/lib/api/stores.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import {
  DEFAULT_INVENTORY_LABEL_CONFIG,
  INVENTORY_LABEL_SIZE_PRESET_OPTIONS,
  mergeInventoryLabelConfig,
  resolveInventoryLabelDimensions,
  type InventoryLabelConfig,
} from '@fixflow/validation';

export function InventoryLabelSettingsSection() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const store = storeQuery.data;

  const [cfg, setCfg] = useState<InventoryLabelConfig>({ ...DEFAULT_INVENTORY_LABEL_CONFIG });
  const [autoLabel, setAutoLabel] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (!store) return;
    setCfg(mergeInventoryLabelConfig(store.inventoryLabelConfig));
    setAutoLabel(Boolean(store.autoLabelOnReceive));
  }, [store]);

  const dims = useMemo(() => resolveInventoryLabelDimensions(cfg), [cfg]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      void previewInventoryLabels({ config: cfg as unknown as Record<string, unknown> })
        .then((r) => {
          if (cancelled) return;
          setPreviewHtml(r.html);
          setWarnings(r.warnings ?? []);
        })
        .catch(() => {
          if (!cancelled) setPreviewHtml(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [cfg]);

  const saveMut = useMutation({
    mutationFn: () =>
      updateCurrentStore({
        inventoryLabelConfig: cfg as unknown as Record<string, unknown>,
        autoLabelOnReceive: autoLabel,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Inventory label settings saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function patch(p: Partial<InventoryLabelConfig>) {
    setCfg((c) => ({ ...c, ...p }));
  }

  const previewScale = Math.min(180 / dims.widthIn, 120 / dims.heightIn);

  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div>
        <h2 className="text-lg font-bold text-slate-950">Inventory barcode labels</h2>
        <p className="mt-1 text-sm text-slate-500">
          Shelf labels for SKU/UPC barcodes. Uses the same PrintNode label printer as repair
          tickets.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Label size</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={cfg.size}
              onChange={(e) => patch({ size: e.target.value as InventoryLabelConfig['size'] })}
            >
              {INVENTORY_LABEL_SIZE_PRESET_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {cfg.size === 'custom' ? (
            <div className="grid grid-cols-2 gap-2">
              <label className="text-sm">
                Width (in)
                <input
                  type="number"
                  step="0.05"
                  min={0.5}
                  max={6}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={cfg.labelWidthIn ?? 2}
                  onChange={(e) => patch({ labelWidthIn: Number(e.target.value) })}
                />
              </label>
              <label className="text-sm">
                Height (in)
                <input
                  type="number"
                  step="0.05"
                  min={0.4}
                  max={6}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={cfg.labelHeightIn ?? 1}
                  onChange={(e) => patch({ labelHeightIn: Number(e.target.value) })}
                />
              </label>
            </div>
          ) : null}

          {(
            [
              ['showName', 'Item name'],
              ['showSku', 'SKU text'],
              ['showBarcode', 'Barcode'],
              ['showPrice', 'Price'],
              ['showBinLocation', 'Bin location'],
              ['showCondition', 'Condition'],
              ['showCompatibility', 'Compatibility'],
              ['showStoreName', 'Store name'],
              ['showDate', 'Print date'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(cfg[key])}
                onChange={(e) => patch({ [key]: e.target.checked })}
              />
              {label}
            </label>
          ))}

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={cfg.showCost}
              onChange={(e) => patch({ showCost: e.target.checked })}
            />
            <span>
              Show cost price
              <span className="mt-0.5 block text-xs font-semibold text-amber-800">
                Warning: cost on a shelf label is visible to customers. Keep off unless labels are
                never customer-facing.
              </span>
            </span>
          </label>

          {cfg.showName ? (
            <label className="block text-sm">
              Name lines
              <input
                type="number"
                min={1}
                max={4}
                className="mt-1 w-24 rounded-xl border border-slate-300 px-3 py-2"
                value={cfg.nameLines}
                onChange={(e) => patch({ nameLines: Number(e.target.value) || 1 })}
              />
            </label>
          ) : null}

          <label className="block text-sm">
            Barcode source
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={cfg.barcodeSource}
              onChange={(e) =>
                patch({ barcodeSource: e.target.value as InventoryLabelConfig['barcodeSource'] })
              }
            >
              <option value="sku">SKU (fallback internal if empty)</option>
              <option value="upc">UPC (fallback SKU if empty)</option>
              <option value="internal_id">Internal barcode / item id</option>
            </select>
          </label>

          <label className="block text-sm">
            Barcode format
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={cfg.barcodeFormat}
              onChange={(e) =>
                patch({ barcodeFormat: e.target.value as InventoryLabelConfig['barcodeFormat'] })
              }
            >
              <option value="CODE128">CODE128 (default — any SKU)</option>
              <option value="EAN13">EAN-13 (12–13 digits or falls back)</option>
              <option value="UPC_A">UPC-A (11–12 digits or falls back)</option>
            </select>
          </label>

          <label className="block text-sm">
            Price source
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={cfg.priceSource}
              onChange={(e) =>
                patch({ priceSource: e.target.value as InventoryLabelConfig['priceSource'] })
              }
              disabled={!cfg.showPrice}
            >
              <option value="retail">Retail</option>
              <option value="promotional">Promotional (in date window) else retail</option>
              <option value="none">None</option>
            </select>
          </label>

          <label className="block text-sm">
            Default copies
            <input
              type="number"
              min={1}
              max={99}
              className="mt-1 w-24 rounded-xl border border-slate-300 px-3 py-2"
              value={cfg.copies}
              onChange={(e) => patch({ copies: Number(e.target.value) || 1 })}
            />
          </label>

          <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <input
              type="checkbox"
              checked={autoLabel}
              onChange={(e) => setAutoLabel(e.target.checked)}
            />
            Auto-print labels after PO receive
          </label>

          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? 'Saving…' : 'Save inventory label settings'}
          </button>
        </div>

        <div>
          <p className="mb-2 text-sm font-semibold text-slate-800">
            Live preview ({dims.widthIn}&quot; × {dims.heightIn}&quot;)
          </p>
          <div className="flex min-h-[140px] items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
            {previewHtml ? (
              <div
                className="overflow-hidden bg-white shadow"
                style={{
                  width: dims.widthIn * previewScale,
                  height: dims.heightIn * previewScale,
                }}
              >
                <iframe
                  title="Inventory label preview"
                  srcDoc={previewHtml}
                  className="border-0"
                  style={{
                    width: `${dims.widthIn}in`,
                    height: `${dims.heightIn}in`,
                    transform: `scale(${previewScale / 96})`,
                    transformOrigin: 'top left',
                    // scale relative: use simpler approach
                    pointerEvents: 'none',
                  }}
                />
              </div>
            ) : (
              <p className="text-sm text-slate-500">Generating preview…</p>
            )}
          </div>
          {warnings.length > 0 ? (
            <p className="mt-2 text-xs font-semibold text-amber-800">{warnings.join(' · ')}</p>
          ) : null}
          <p className="mt-2 text-xs text-slate-500">
            Preview uses sample SKU/UPC data. Actual item fields apply when printing.
          </p>
        </div>
      </div>
    </section>
  );
}
